import React from 'react'
import BkPixel, { BK_INK } from './BkPixel'
import { BK_STRIP_ICON, besideEachOther, priceDigits, stitchText } from './pixelType'
import { BK_HIT, bkHit } from './layout'
import type { BkPrintOption } from './printOptions'
import { tornPaper } from './paper'

const TAG_W = 290
const TAG_H = 100
/** Pinned by hand, so no two hang at the same angle. */
const TILT = [-2.5, 1.8, -1.4, 2.2, -1.8]

/** Up to four strips are drawn one by one; beyond that, "5x" and one strip. */
function stripsArt(quantity: number): ReturnType<typeof besideEachOther> {
  if (quantity <= 4) return besideEachOther(Array.from({ length: quantity }, () => BK_STRIP_ICON))
  return besideEachOther([stitchText(`${quantity}x`), BK_STRIP_ICON])
}

/**
 * "How many prints" for the payment plate, drawn in its own vocabulary: paper
 * notes pinned to the wallpaper, one per print package, each showing its strips
 * and its price in block lettering. The chosen note is stitched round in the
 * lettering ink. There is no copy on them — a glyph, not a sentence.
 */
const BkPrintTags: React.FC<{
  options: BkPrintOption[]
  selected: number
  onSelect: (quantity: number) => void
  disabled?: boolean
}> = ({ options, selected, onSelect, disabled }) => (
  <div
    style={bkHit(BK_HIT.payment.prints)}
    className="flex flex-col items-center justify-center gap-4"
  >
    {options.map((option, i) => {
      const on = option.quantity === selected
      return (
        <button
          key={option.quantity}
          aria-label={`${option.quantity} print${option.quantity > 1 ? 's' : ''}, RM ${option.price}`}
          aria-pressed={on}
          disabled={disabled}
          onClick={() => onSelect(option.quantity)}
          className="relative shrink-0 cursor-pointer bg-transparent p-0 transition-transform duration-150 disabled:cursor-default"
          style={{
            width: TAG_W,
            height: TAG_H,
            transform: `rotate(${on ? 0 : TILT[i % TILT.length]}deg) scale(${on ? 1.05 : 1})`,
            filter: `drop-shadow(2px ${on ? 7 : 4}px ${on ? 7 : 4}px rgba(60, 20, 10, ${on ? 0.35 : 0.25}))`
          }}
        >
          <svg
            width={TAG_W}
            height={TAG_H}
            viewBox={`0 0 ${TAG_W} ${TAG_H}`}
            className="absolute inset-0"
            aria-hidden
          >
            <defs>
              <linearGradient id={`bk-paper-${i}`} x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stopColor="#FFFDFB" />
                <stop offset="1" stopColor="#F3EEE8" />
              </linearGradient>
            </defs>
            <path d={tornPaper(TAG_W, TAG_H, i)} fill={`url(#bk-paper-${i})`} />
            {on && (
              <rect
                x={12}
                y={12}
                width={TAG_W - 24}
                height={TAG_H - 24}
                fill="none"
                stroke={BK_INK}
                strokeWidth={3}
                strokeDasharray="8 6"
              />
            )}
          </svg>
          <span className="relative flex h-full items-center justify-between px-8">
            <BkPixel rows={stripsArt(option.quantity)} block={4} />
            <BkPixel rows={stitchText(`RM${priceDigits(option.price)}`)} block={5} />
          </span>
        </button>
      )
    })}
  </div>
)

export default BkPrintTags
